import { useCallback } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Switch,
} from '@pantry/ui';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '@pantry/api-client';
import { toast } from 'sonner';
import {
  BellIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  DevicePhoneMobileIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@pantry/ui';
import { CURATED_TIMEZONES, getTimezoneLabel } from '@pantry/shared';

// Use curated timezones from shared package
const TIMEZONE_OPTIONS = CURATED_TIMEZONES;

function formatTimeForInput(timeStr: string | null): string {
  return timeStr ?? '22:00';
}

function formatTimeDisplay(timeStr: string | null, locale: string): string {
  // Default to 9:00 PM (21:00) for display
  const time = timeStr ?? '21:00';
  const [hours, minutes] = time.split(':').map(Number);
  // Create a date object to use locale-aware time formatting
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}

export function NotificationsSection() {
  const { t, i18n } = useLingui();
  const { data: preferences, isLoading } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();

  // Notification types configuration - inside component for translation
  const notificationTypes = [
    {
      id: 'expiryReminders',
      label: t`Expiry Reminders`,
      description: t`Get notified before items expire`,
      icon: ClockIcon,
      pushField: 'expiryRemindersPush' as const,
    },
    {
      id: 'lowStockAlerts',
      label: t`Low Stock Alerts`,
      description: t`When items run below threshold`,
      icon: ExclamationTriangleIcon,
      pushField: 'lowStockAlertsPush' as const,
    },
    {
      id: 'sharingInvitations',
      label: t`Sharing Invitations`,
      description: t`Household invite notifications`,
      icon: UserGroupIcon,
      pushField: 'sharingInvitationsPush' as const,
    },
  ];

  const reminderDayOptions = [
    { value: 1, label: t`1 day` },
    { value: 3, label: t`3 days` },
    { value: 7, label: t`1 week` },
    { value: 14, label: t`2 weeks` },
  ];

  const handleChannelToggle = useCallback(
    async (field: 'expiryRemindersPush' | 'lowStockAlertsPush' | 'sharingInvitationsPush') => {
      const currentValue = preferences?.[field] ?? true;
      try {
        await updatePreferences.mutateAsync({
          [field]: !currentValue,
        });
        toast.success(t`Notification preference updated`);
      } catch (error) {
        const message = error instanceof Error ? error.message : t`Failed to update preference`;
        toast.error(message);
      }
    },
    [preferences, updatePreferences, t]
  );

  const handleReminderDayToggle = useCallback(
    async (day: number) => {
      const currentDays = preferences?.reminderDays ?? [1, 3, 7];
      const newDays = currentDays.includes(day)
        ? currentDays.filter((d) => d !== day)
        : [...currentDays, day].sort((a, b) => a - b);

      if (newDays.length === 0) {
        toast.error(t`At least one reminder day must be selected`);
        return;
      }

      try {
        await updatePreferences.mutateAsync({
          reminderDays: newDays,
        });
        toast.success(t`Reminder days updated`);
      } catch (error) {
        const message = error instanceof Error ? error.message : t`Failed to update reminder days`;
        toast.error(message);
      }
    },
    [preferences?.reminderDays, updatePreferences, t]
  );

  const handleQuietHoursToggle = useCallback(async () => {
    const hasQuietHours = !!(preferences?.quietHoursStart && preferences?.quietHoursEnd);

    try {
      if (hasQuietHours) {
        await updatePreferences.mutateAsync({
          quietHoursStart: null,
          quietHoursEnd: null,
        });
        toast.success(t`Quiet hours disabled`);
      } else {
        await updatePreferences.mutateAsync({
          quietHoursStart: '22:00',
          quietHoursEnd: '08:00',
        });
        toast.success(t`Quiet hours enabled`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to update quiet hours`;
      toast.error(message);
    }
  }, [preferences, updatePreferences, t]);

  const handleTimeChange = useCallback(
    async (type: 'start' | 'end', value: string) => {
      try {
        await updatePreferences.mutateAsync({
          [type === 'start' ? 'quietHoursStart' : 'quietHoursEnd']: value,
        });
        toast.success(t`Quiet hours updated`);
      } catch (error) {
        const message = error instanceof Error ? error.message : t`Failed to update time`;
        toast.error(message);
      }
    },
    [updatePreferences, t]
  );

  const handleTimezoneChange = useCallback(
    async (timezone: string) => {
      try {
        await updatePreferences.mutateAsync({ timezone });
        toast.success(t`Timezone updated`);
      } catch (error) {
        const message = error instanceof Error ? error.message : t`Failed to update timezone`;
        toast.error(message);
      }
    },
    [updatePreferences, t]
  );

  const handleNotificationTimeChange = useCallback(
    async (time: string) => {
      try {
        await updatePreferences.mutateAsync({ notificationTime: time });
        toast.success(t`Notification time updated`);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : t`Failed to update notification time`;
        toast.error(message);
      }
    },
    [updatePreferences, t]
  );

  const hasQuietHours = !!(preferences?.quietHoursStart && preferences?.quietHoursEnd);
  const reminderDays = preferences?.reminderDays ?? [1, 3, 7];
  const hasAnyPushEnabled =
    (preferences?.expiryRemindersPush ?? true) ||
    (preferences?.lowStockAlertsPush ?? true) ||
    (preferences?.sharingInvitationsPush ?? true);

  if (isLoading) {
    return (
      <Card className="rounded-xl animate-pulse" data-tour="settings-notifications">
        <CardHeader>
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="h-4 w-64 bg-muted rounded mt-2" />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="h-32 bg-muted rounded-xl" />
          <div className="h-32 bg-muted rounded-xl" />
          <div className="h-32 bg-muted rounded-xl" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl" data-tour="settings-notifications">
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
            <BellIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <Trans>Notifications</Trans>
        </CardTitle>
        <CardDescription>
          <Trans>Configure how and when you receive notifications about your inventory.</Trans>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Notification Types with Channel Toggles */}
        <div className="space-y-4">
          {notificationTypes.map((type) => {
            const Icon = type.icon;
            const isPushEnabled = preferences?.[type.pushField] ?? true;

            return (
              <div key={type.id} className="rounded-xl border bg-muted/30 p-4 space-y-3">
                {/* Type Header */}
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-xl transition-colors',
                      isPushEnabled ? 'bg-primary/20' : 'bg-muted'
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-5 w-5 transition-colors',
                        isPushEnabled ? 'text-primary' : 'text-muted-foreground'
                      )}
                    />
                  </div>
                  <div>
                    <span className="font-medium text-foreground">{type.label}</span>
                    <p className="text-sm text-muted-foreground">{type.description}</p>
                  </div>
                </div>

                {/* Channel Row - Push */}
                <label
                  htmlFor={`${type.id}-push`}
                  className={cn(
                    'flex items-center justify-between rounded-xl border p-3 cursor-pointer transition-colors hover:bg-muted/50',
                    isPushEnabled ? 'border-primary/30 bg-primary/5' : 'border-border bg-background'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                        isPushEnabled ? 'bg-primary/15' : 'bg-muted'
                      )}
                    >
                      <DevicePhoneMobileIcon
                        className={cn(
                          'h-4 w-4 transition-colors',
                          isPushEnabled ? 'text-primary' : 'text-muted-foreground'
                        )}
                      />
                    </div>
                    <span className="text-sm text-foreground">
                      <Trans>Push Notifications</Trans>
                    </span>
                  </div>
                  <Switch
                    id={`${type.id}-push`}
                    checked={isPushEnabled}
                    onCheckedChange={() => handleChannelToggle(type.pushField)}
                    disabled={updatePreferences.isPending}
                  />
                </label>

                {/* Future: Email channel would go here */}
              </div>
            );
          })}
        </div>

        {/* Reminder Days - only show if expiry reminders push is enabled */}
        {(preferences?.expiryRemindersPush ?? true) && (
          <div className="space-y-3 pt-4 border-t">
            <div>
              <Label className="text-base">
                <Trans>Reminder Timing</Trans>
              </Label>
              <p className="text-sm text-muted-foreground">
                <Trans>Choose when to receive expiry reminders before items expire.</Trans>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {reminderDayOptions.map((option) => {
                const isSelected = reminderDays.includes(option.value);
                return (
                  <button
                    key={option.value}
                    onClick={() => handleReminderDayToggle(option.value)}
                    disabled={updatePreferences.isPending}
                    className={cn(
                      'px-4 py-2 rounded-xl text-sm font-medium transition-all',
                      'border focus:outline-none focus:ring-2 focus:ring-primary/50',
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90'
                        : 'bg-muted text-foreground border-border hover:bg-muted/80',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Quiet Hours - only relevant for push */}
        {hasAnyPushEnabled && (
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">
                  <Trans>Quiet Hours</Trans>
                </Label>
                <p className="text-sm text-muted-foreground">
                  <Trans>Pause push notifications during specific hours.</Trans>
                </p>
              </div>
              <Switch
                checked={hasQuietHours}
                onCheckedChange={handleQuietHoursToggle}
                disabled={updatePreferences.isPending}
              />
            </div>

            {hasQuietHours && (
              <div className="rounded-xl bg-muted/50 p-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  <Trans>No push notifications will be sent during quiet hours.</Trans>
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quiet-start" className="text-sm">
                      <Trans>From</Trans>
                    </Label>
                    <input
                      type="time"
                      id="quiet-start"
                      value={formatTimeForInput(preferences?.quietHoursStart ?? null)}
                      onChange={(e) => handleTimeChange('start', e.target.value)}
                      disabled={updatePreferences.isPending}
                      className="flex h-10 w-full rounded-xl border border-border/50 bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quiet-end" className="text-sm">
                      <Trans>Until</Trans>
                    </Label>
                    <input
                      type="time"
                      id="quiet-end"
                      value={formatTimeForInput(preferences?.quietHoursEnd ?? null)}
                      onChange={(e) => handleTimeChange('end', e.target.value)}
                      disabled={updatePreferences.isPending}
                      className="flex h-10 w-full rounded-xl border border-border/50 bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notification Time */}
        {hasAnyPushEnabled && (
          <div className="space-y-3 pt-4 border-t">
            <div>
              <Label className="text-base">
                <Trans>Notification Time</Trans>
              </Label>
              <p className="text-sm text-muted-foreground">
                <Trans>Daily expiry reminders and alerts will be sent at this time.</Trans>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 shrink-0">
                <ClockIcon className="h-5 w-5 text-primary" />
              </div>
              <input
                type="time"
                value={preferences?.notificationTime ?? '09:00'}
                onChange={(e) => handleNotificationTimeChange(e.target.value)}
                disabled={updatePreferences.isPending}
                className="flex-1 h-10 rounded-xl border border-border/50 bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>
        )}

        {/* Timezone */}
        {hasAnyPushEnabled && (
          <div className="space-y-3 pt-4 border-t">
            <div>
              <Label className="text-base">
                <Trans>Timezone</Trans>
              </Label>
              <p className="text-sm text-muted-foreground">
                <Trans>Notifications will be delivered based on this timezone.</Trans>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 shrink-0">
                <GlobeAltIcon className="h-5 w-5 text-primary" />
              </div>
              <select
                value={preferences?.timezone ?? 'UTC'}
                onChange={(e) => handleTimezoneChange(e.target.value)}
                disabled={updatePreferences.isPending}
                className="flex-1 h-10 rounded-xl border border-border/50 bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem',
                }}
              >
                {(['Americas', 'Europe', 'Asia', 'Pacific', 'Other'] as const).map((region) => {
                  const regionTimezones = TIMEZONE_OPTIONS.filter((tz) => tz.region === region);
                  if (regionTimezones.length === 0) return null;

                  const regionLabels: Record<string, string> = {
                    Americas: t`Americas`,
                    Europe: t`Europe`,
                    Asia: t`Asia`,
                    Pacific: t`Pacific`,
                    Other: t`Other`,
                  };

                  return (
                    <optgroup key={region} label={regionLabels[region]}>
                      {regionTimezones.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
            </div>
          </div>
        )}

        {/* Info Note */}
        <div className="text-xs text-muted-foreground pt-2 flex items-start gap-2">
          <BellIcon className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            {t`Expiry reminders are sent daily at ${formatTimeDisplay(preferences?.notificationTime ?? null, i18n.locale)} (${getTimezoneLabel(preferences?.timezone ?? null)}). To receive push notifications on mobile, enable them in the mobile app settings.`}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
